import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { FieldsetModule } from 'primeng/fieldset';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { GoogleMapComponent } from '../../shared/google-map/google-map.component';
import { LatLng } from '../../models/common/lat-lng.model';
import { SharedEndpointService } from '../../services/shared-endpoint.service';
import { Company } from '../../company/company.model';
import { LocationSearchComponent } from '../../shared/location-search/location-search.component';
import { CheckboxModule } from 'primeng/checkbox';
import { CompanyService } from '../../company/company.service';
import { AuthService, SocialProvider } from '../auth.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { firstValueFrom } from 'rxjs';
import { User } from '@angular/fire/auth';
import { LanguageService } from '../../services/language.service';
import { SessionStorageService } from '../../helpers/session-storage.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
	selector: 'app-register',
	templateUrl: './register.html',
	styleUrls: ['./register.css'],
	imports: [
		ReactiveFormsModule,
		ButtonModule,
		InputTextModule,
		InputMaskModule,
		DividerModule,
		FieldsetModule,
		InputGroupModule,
		InputGroupAddonModule,
		IftaLabelModule,
		GoogleMapComponent,
		LocationSearchComponent,
		CheckboxModule,
		ToastModule,
		TranslateModule
	],
	providers: [MessageService],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterComponent implements OnInit {

	createEditCompany: Company = new Company();

	formBuilder = inject(FormBuilder);
	companyService = inject(CompanyService);
	authService = inject(AuthService);
	messageService = inject(MessageService);
	sharedEndpointService = inject(SharedEndpointService);
	route = inject(ActivatedRoute);
	router = inject(Router);
	languageService = inject(LanguageService);
	sessionStorageService = inject(SessionStorageService);

	form!: FormGroup<any>;
	isSocialSignIn = false;
	isExistingFirebaseUser = false;
	registering = false;
	registrationCompleted = false;
	registrationCompletionMessage = '';
	socialProvider: SocialProvider | null = null;
	private socialUser: User | null = null;
	private returnUrl = '/loads';
	mapLatitude = 24.702575;
	mapLongitude = 46.6406628;
	registeredAddressMissing = false;
	private motLicenseSyncedToCr = true;

	ngOnInit(): void {
		this.form = this.formBuilder.group({
			companyName: ['', Validators.required],
			crNumber: ['', Validators.required],
			vatNumber: ['', Validators.required],
			searchLocation: [''],
			fullName: ['', Validators.required],
			mobileNumber: ['', Validators.required],
			email: ['', [Validators.required, Validators.email]],
			websiteURL: [''],
			isTransportationAgency: [true],
			isFreightForwarder: [true],
			motAgencyLicenseNumber: ['']
		});

		this.syncMotLicenseWithCrNumber();
		this.enableEmailMode();
		this.applyLoginRedirectContext();
	}

	private applyLoginRedirectContext(): void {
		const email = this.route.snapshot.queryParamMap.get('email');
		const shouldUseCurrentFirebaseUser = this.route.snapshot.queryParamMap.get('firebaseUser') === 'current';
		this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/loads';
		const currentUser = this.authService.getCurrentUser();

		if (email) {
			this.form.patchValue({ email });
		}

		if (!shouldUseCurrentFirebaseUser || !currentUser) {
			return;
		}

		this.isExistingFirebaseUser = true;
		const detectedProvider = this.detectSocialProvider(currentUser);
		this.isSocialSignIn = detectedProvider !== null;
		this.socialProvider = detectedProvider;
		this.form.patchValue({
			email: currentUser.email ?? email ?? '',
			fullName: currentUser.displayName ?? this.form.get('fullName')?.value,
			mobileNumber: currentUser.phoneNumber ?? this.form.get('mobileNumber')?.value
		});

		const emailControl = this.form.get('email');
		if (currentUser.email) {
			emailControl?.disable({ emitEvent: false });
		} else {
			emailControl?.enable({ emitEvent: false });
		}
		emailControl?.updateValueAndValidity({ emitEvent: false });

		this.messageService.add({
			severity: 'info',
			summary: 'Complete registration',
			detail: 'We found your sign-in account. Complete your company profile to continue.'
		});
	}

	placeSelect(result: google.maps.places.Place | google.maps.GeocoderResult): void {
		const location = this.extractPlaceLocation(result);
		if (!result || !location) {
			return;
		}

		const lat = location.lat();
		const lng = location.lng();
		const displayName = this.extractPlaceName(result);
		const formattedAddress = this.extractPlaceAddress(result);
		const phoneNumber = this.extractPlacePhoneNumber(result);
		const websiteURL = this.extractPlaceWebsite(result);
		const placeId = this.extractPlaceId(result);

		this.mapLatitude = lat;
		this.mapLongitude = lng;
		this.createEditCompany.name = displayName ?? '';
		this.createEditCompany.location = new LatLng(lat, lng, displayName ?? formattedAddress ?? '');
		this.createEditCompany.address = formattedAddress ?? '';
		this.createEditCompany.phoneNumber = phoneNumber?.replace(/\s/g, '') ?? null;
		this.createEditCompany.websiteURL = websiteURL ?? null;
		this.createEditCompany.placeId = placeId ?? null;
		this.createEditCompany.logo = this.extractPlacePhotoUrl(result);
		this.createEditCompany.countryId = 2;

		const countryCode = this.extractPlaceCountryCode(result);
		this.applyCountryFromCode(countryCode);

		this.patchPlaceDetails(displayName, formattedAddress, websiteURL);
		this.registeredAddressMissing = false;
	}

	async signInWithSocial(provider: SocialProvider): Promise<void> {
		try {
			this.isSocialSignIn = false;
			this.isExistingFirebaseUser = false;
			this.socialProvider = null;
			this.socialUser = null;

			const user = await firstValueFrom(this.authService.signInWithSocialProvider(provider));
			if (!user) {
				throw new Error('Social sign-in did not return a user.');
			}

			this.socialProvider = provider;
			this.socialUser = user;
			this.isSocialSignIn = true;

			this.form.patchValue({
				email: user.email ?? this.form.get('email')?.value,
				fullName: user.displayName ?? this.form.get('fullName')?.value
			});

			const emailControl = this.form.get('email');
			if (user.email) {
				emailControl?.disable({ emitEvent: false });
			} else {
				emailControl?.enable({ emitEvent: false });
				emailControl?.markAsTouched();
			}
			emailControl?.updateValueAndValidity({ emitEvent: false });

			this.messageService.add({
				severity: 'success',
				summary: 'Signed in',
				detail: `${this.providerLabel(provider)} sign-in completed.`
			});
		} catch (error: any) {
			this.enableEmailMode();
			console.error('Social sign-in failed', error);
			this.messageService.add({
				severity: 'error',
				summary: 'Social sign-in failed',
				detail: error?.message || 'Unable to sign in with the selected provider.'
			});
		}
	}

	private enableEmailMode(): void {
		this.isSocialSignIn = false;
		this.socialProvider = null;
		this.socialUser = null;

		const emailControl = this.form.get('email');
		emailControl?.enable({ emitEvent: false });
		emailControl?.setValidators([Validators.required, Validators.email]);
		emailControl?.updateValueAndValidity({ emitEvent: false });
	}

	async register() {
		if (this.registering) {
			return;
		}

		this.registeredAddressMissing = this.isRegisteredAddressMissing();
		if (this.registeredAddressMissing) {
			this.form.get('searchLocation')?.markAsTouched();
		}

		if (this.form.invalid || this.registeredAddressMissing) {
			this.form.markAllAsTouched();
			this.messageService.add({
				severity: 'error',
				summary: 'Missing information',
				detail: 'Please complete the required fields before creating the account.'
			});
			return;
		}

		this.registering = true;

		try {
			const formValue = this.form.getRawValue();
			const existingFirebaseId = this.resolveExistingFirebaseId();
			const response = await firstValueFrom(this.companyService.selfRegister({
				existingFirebaseId,
				sendSetupEmail: !this.isUsingSocialFirebaseIdentity(),
				customer: {
					name: formValue.fullName,
					emailAddress: formValue.email,
					phoneNumber: formValue.mobileNumber,
					corporateRole: 'ADMIN',
					defaultLanguage: this.languageService.currentLanguage(),
					countryId: this.createEditCompany.countryId ?? null
				},
				company: {
					name: formValue.companyName,
					crNumber: Number(formValue.crNumber),
					taxRegNumber: formValue.vatNumber,
					phoneNumber: this.blankToNull(this.createEditCompany.phoneNumber),
					websiteURL: this.blankToNull(formValue.websiteURL),
					emailAddress: null,
					address: this.blankToNull(this.createEditCompany.address),
					placeId: this.blankToNull(this.createEditCompany.placeId),
					logo: this.blankToNull(this.createEditCompany.logo),
					location: this.createEditCompany.location ?? null,
					countryId: this.createEditCompany.countryId ?? null
				},
				transportationAgency: {
					enabled: !!formValue.isTransportationAgency,
					motAgencyLicenseNumber: this.blankToNull(formValue.motAgencyLicenseNumber || formValue.crNumber)
				},
				freightForwarder: {
					enabled: !!formValue.isFreightForwarder
				}
			}));

			if (!response.success || !response.data) {
				throw new Error(response.message || 'Registration failed');
			}

			const result = response.data;

			if (existingFirebaseId) {
				const currentUser = this.authService.getCurrentUser();
				if (currentUser) {
					this.sessionStorageService.saveUser(currentUser);
				}
				this.sessionStorageService.saveCustomer(result.customer);
				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: result.setupEmailSent
						? 'Registration successful. Your account setup email is on its way.'
						: 'Registration successful!'
				});
				await this.router.navigateByUrl(this.returnUrl);
				return;
			}

			this.registrationCompleted = true;
			this.registrationCompletionMessage = result.setupEmailSent
				? 'We created your company profile and sent a secure setup email. Open the link in that email to create your password and sign in.'
				: 'We created your company profile. Sign in from the login page to continue.';
			this.messageService.add({
				severity: 'success',
				summary: 'Registration submitted',
				detail: result.setupEmailSent
					? 'Check your email to finish setting up your account.'
					: 'Registration successful.'
			});
		} catch (error: any) {
			console.error('Registration failed', error);
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: error?.message || 'Registration failed'
			});
		} finally {
			this.registering = false;
		}
	}

	goToLogin(): void {
		void this.router.navigate(['/login']);
	}

	private resolveExistingFirebaseId(): string | null {
		if (this.isSocialSignIn) {
			return this.socialUser?.uid ?? null;
		}

		if (this.isExistingFirebaseUser) {
			return this.authService.getCurrentUser()?.uid ?? null;
		}

		return null;
	}

	private isUsingSocialFirebaseIdentity(): boolean {
		if (this.isSocialSignIn) {
			return true;
		}

		if (!this.isExistingFirebaseUser) {
			return false;
		}

		return this.isCurrentFirebaseUserSocial();
	}

	private isCurrentFirebaseUserSocial(): boolean {
		const currentUser = this.authService.getCurrentUser();
		if (!currentUser) {
			return false;
		}

		return currentUser.providerData.some(provider => {
			const providerId = provider?.providerId ?? '';
			return providerId !== 'password' && providerId !== 'phone';
		});
	}

	providerLabel(provider: SocialProvider): string {
		switch (provider) {
			case 'google':
				return 'Google';
			case 'microsoft':
				return 'Microsoft';
			case 'apple':
				return 'Apple';
			case 'facebook':
				return 'Facebook';
			case 'twitter':
				return 'Twitter';
		}
	}

	private detectSocialProvider(user: User): SocialProvider | null {
		for (const provider of user.providerData) {
			switch (provider?.providerId) {
				case 'google.com':
					return 'google';
				case 'microsoft.com':
					return 'microsoft';
				case 'apple.com':
					return 'apple';
				case 'facebook.com':
					return 'facebook';
				case 'twitter.com':
					return 'twitter';
				default:
					break;
			}
		}

		return null;
	}

	private blankToNull(value: string | null | undefined): string | null {
		const trimmed = value?.trim();
		return trimmed ? trimmed : null;
	}

	isInvalid(controlName: string): boolean {
		if (controlName === 'searchLocation') {
			return this.registeredAddressMissing;
		}

		const control = this.form.get(controlName);
		return !!control && control.invalid && (control.touched || control.dirty);
	}

	private syncMotLicenseWithCrNumber(): void {
		this.form.get('crNumber')?.valueChanges.subscribe(value => {
			const motControl = this.form.get('motAgencyLicenseNumber');
			if (!motControl) {
				return;
			}

			if (this.motLicenseSyncedToCr || !motControl.value) {
				motControl.setValue(value ?? '', { emitEvent: false });
				this.motLicenseSyncedToCr = true;
			}
		});

		this.form.get('motAgencyLicenseNumber')?.valueChanges.subscribe(value => {
			const crNumber = this.form.get('crNumber')?.value ?? '';
			this.motLicenseSyncedToCr = !value || value === crNumber;
		});
	}

	private isRegisteredAddressMissing(): boolean {
		const selectedAddress = String(this.createEditCompany.address ?? '').trim();
		const typedAddress = String(this.form.get('searchLocation')?.value ?? '').trim();
		return !selectedAddress && !typedAddress;
	}

	private applyCountryFromCode(countryCode: string | null): void {
		if (!countryCode || !this.sessionStorageService.getAccessToken()) {
			return;
		}

		this.sharedEndpointService.findCountryByCode(countryCode, 'en-US').subscribe({
			next: country => {
				if (country?.id) {
					this.createEditCompany.countryId = country.id;
				}
			},
			error: () => {
				// Registration is public; keep the default country when protected lookup is unavailable.
			}
		});
	}

	private extractPlaceLocation(result: google.maps.places.Place | google.maps.GeocoderResult | null | undefined): google.maps.LatLng | null {
		const place = result as google.maps.places.Place | undefined;
		const geocoderResult = result as google.maps.GeocoderResult | undefined;
		const legacyPlace = result as google.maps.places.PlaceResult | undefined;
		return place?.location ?? legacyPlace?.geometry?.location ?? geocoderResult?.geometry?.location ?? null;
	}

	private extractPlaceName(result: google.maps.places.Place | google.maps.GeocoderResult): string | null {
		const place = result as any;
		return place.displayName ?? place.name ?? null;
	}

	private extractPlaceAddress(result: google.maps.places.Place | google.maps.GeocoderResult): string | null {
		const place = result as google.maps.places.Place;
		const geocoderResult = result as google.maps.GeocoderResult;
		const legacyPlace = result as google.maps.places.PlaceResult;
		return place.formattedAddress ?? legacyPlace.formatted_address ?? geocoderResult.formatted_address ?? null;
	}

	private extractPlacePhoneNumber(result: google.maps.places.Place | google.maps.GeocoderResult): string | null {
		const place = result as any;
		return place.internationalPhoneNumber ?? place.international_phone_number ?? place.formatted_phone_number ?? null;
	}

	private extractPlaceWebsite(result: google.maps.places.Place | google.maps.GeocoderResult): string | null {
		const place = result as any;
		return place.websiteURI ?? place.website ?? null;
	}

	private extractPlaceId(result: google.maps.places.Place | google.maps.GeocoderResult): string | null {
		const place = result as google.maps.places.Place;
		const geocoderResult = result as google.maps.GeocoderResult;
		const legacyPlace = result as google.maps.places.PlaceResult;
		return place.id ?? legacyPlace.place_id ?? geocoderResult.place_id ?? null;
	}

	private extractPlacePhotoUrl(result: google.maps.places.Place | google.maps.GeocoderResult): string | null {
		const photo = (result as any).photos?.[0];
		return photo?.getURI?.() ?? photo?.getUrl?.() ?? null;
	}

	private extractPlaceCountryCode(result: google.maps.places.Place | google.maps.GeocoderResult): string | null {
		const place = result as google.maps.places.Place;
		const geocoderResult = result as google.maps.GeocoderResult;
		const components = place.addressComponents ?? geocoderResult.address_components ?? [];
		const country = components.find((component: any) => component.types?.includes('country')) as any;
		return country?.shortText ?? country?.short_name ?? null;
	}

	private patchPlaceDetails(displayName: string | null, formattedAddress: string | null, websiteURL: string | null): void {
		const patch: Record<string, string> = {};
		if (displayName) {
			patch['companyName'] = displayName;
		}
		if (websiteURL) {
			patch['websiteURL'] = websiteURL;
		}
		this.form.patchValue(patch);

		if (formattedAddress) {
			const addressControl = this.form.get('searchLocation');
			addressControl?.setValue(formattedAddress);
			addressControl?.markAsDirty();
			addressControl?.updateValueAndValidity();
		}
	}
}
